/**
 * Pure(ish) operations over the editor document for comment/suggestion marks:
 * locate a thread's ranges, read the LIVE anchored text, and apply the
 * accept/reject outcome of a suggestion. All transactions carry
 * `SUGGESTION_SKIP_META` so suggestion mode never re-tracks its own edits.
 */
import type { Editor } from "@tiptap/core";
import type { Mark, Node as PMNode } from "@tiptap/pm/model";
import type { CommentThread } from "@folio/shared";
import {
  COMMENT_MARK,
  markIdAttribute,
  SUGGESTION_DELETE_MARK,
  SUGGESTION_INSERT_MARK,
} from "./marks";
import { SUGGESTION_SKIP_META } from "./suggestion-mode";

export interface MarkRange {
  from: number;
  to: number;
  text: string;
  mark: Mark;
}

/** Contiguous ranges of text carrying mark `markName` with the given id. */
export function findMarkRanges(doc: PMNode, markName: string, id: string): MarkRange[] {
  const attr = markIdAttribute(markName);
  const ranges: MarkRange[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) {
      return true;
    }
    const mark = node.marks.find((m) => m.type.name === markName && m.attrs[attr] === id);
    if (!mark) {
      return true;
    }
    const from = pos;
    const to = pos + node.nodeSize;
    const last = ranges[ranges.length - 1];
    if (last && last.to === from) {
      last.to = to;
      last.text += node.text ?? "";
    } else {
      ranges.push({ from, to, text: node.text ?? "", mark });
    }
    return true;
  });
  return ranges;
}

/** The text currently anchored by a thread's mark ("" when the mark is gone). */
export function markedText(doc: PMNode, markName: string, id: string): string {
  return findMarkRanges(doc, markName, id)
    .map((range) => range.text)
    .join(" ");
}

/** Mark name that anchors a given thread. */
export function threadMarkName(thread: Pick<CommentThread, "kind" | "suggestion">): string {
  if (thread.kind === "comment") {
    return COMMENT_MARK;
  }
  return thread.suggestion?.action === "delete" ? SUGGESTION_DELETE_MARK : SUGGESTION_INSERT_MARK;
}

function removeRanges(editor: Editor, ranges: MarkRange[]): void {
  if (ranges.length === 0) {
    return;
  }
  const tr = editor.state.tr;
  tr.setMeta(SUGGESTION_SKIP_META, true);
  // Descending order: earlier deletions must not shift later positions.
  for (const range of ranges.toSorted((a, b) => b.from - a.from)) {
    tr.delete(range.from, range.to);
  }
  editor.view.dispatch(tr);
}

function unmarkRanges(editor: Editor, ranges: MarkRange[]): void {
  if (ranges.length === 0) {
    return;
  }
  const tr = editor.state.tr;
  tr.setMeta(SUGGESTION_SKIP_META, true);
  for (const range of ranges) {
    // Remove by mark INSTANCE: other threads' marks of the same type survive.
    tr.removeMark(range.from, range.to, range.mark);
  }
  editor.view.dispatch(tr);
}

/**
 * Applies the outcome of a suggestion:
 *  - accept insert → text stays, mark removed
 *  - reject insert → proposed text is deleted
 *  - accept delete → struck text is deleted
 *  - reject delete → text stays, mark removed
 */
export function applySuggestionOutcome(
  editor: Editor,
  thread: Pick<CommentThread, "id" | "kind" | "suggestion">,
  accept: boolean,
): void {
  const action = thread.suggestion?.action ?? "insert";
  const markName = action === "delete" ? SUGGESTION_DELETE_MARK : SUGGESTION_INSERT_MARK;
  const ranges = findMarkRanges(editor.state.doc, markName, thread.id);
  const keepText = (action === "insert") === accept;
  if (keepText) {
    unmarkRanges(editor, ranges);
  } else {
    removeRanges(editor, ranges);
  }
}

/** Removes a comment thread's anchor marks (resolve/delete thread). */
export function removeCommentMarks(editor: Editor, threadId: string): void {
  unmarkRanges(editor, findMarkRanges(editor.state.doc, COMMENT_MARK, threadId));
}

/** Applies the comment mark for `threadId` over the current selection. */
export function markSelectionWithComment(editor: Editor, threadId: string): boolean {
  const { from, to } = editor.state.selection;
  if (to <= from) {
    return false;
  }
  const type = editor.schema.marks[COMMENT_MARK];
  if (!type) {
    return false;
  }
  const tr = editor.state.tr;
  tr.setMeta(SUGGESTION_SKIP_META, true);
  tr.addMark(from, to, type.create({ threadId }));
  editor.view.dispatch(tr);
  return true;
}

/** Text of the current selection (anchor snapshot for a new thread). */
export function selectionText(editor: Editor): string {
  const { from, to } = editor.state.selection;
  return editor.state.doc.textBetween(from, to, " ");
}
